import { Button, Drawer, Form, Select, Space, Spin, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { useEmployeeNames } from "@/shared/hooks/useEmployeeNames";
import {
	useAssignmentTargetOptions,
	useLeadFormAssignments,
	useReplaceLeadFormAssignments,
} from "../hooks/useLeadForms";
import type { LeadForm, LeadFormAssignmentKind } from "../types";

interface Props {
	form: LeadForm | null;
	onClose: () => void;
}

type Selection = Record<LeadFormAssignmentKind, string[]>;

/**
 * ⚠️ Modul darajasidagi konstanta EMAS — FABRIKA. Boshlang'ich holat
 * to'g'ridan-to'g'ri umumiy obyekt bilan berilsa, uning massivlariga
 * `push` qiladigan har qanday keyingi tahrir konstantani BUZIB, keyingi
 * ochilishlarda eski tanlovlar «sizib» chiqardi (populate tarmog'i
 * aynan `push` ishlatadi).
 */
const emptySelection = (): Selection => ({ employee: [], department: [], position: [] });

const EMPLOYEE_SEARCH_DEBOUNCE_MS = 300;

/**
 * ⚠️ `Record<LeadFormAssignmentKind, null>` — union kengaysa, shu obyekt
 * tuzilmaviy tekshiruvdan o'tmay qoladi (exhaustiveness guard). Oddiy
 * `LeadFormAssignmentKind[]` massiv bo'lganda yangi kind jimgina
 * tashlab ketilishi mumkin edi.
 */
const ASSIGNMENT_KIND_SET: Record<LeadFormAssignmentKind, null> = {
	employee: null,
	department: null,
	position: null,
};
const ASSIGNMENT_KINDS = Object.keys(ASSIGNMENT_KIND_SET) as LeadFormAssignmentKind[];

/**
 * ⚠️ `PUT /lead-forms/{id}/assignments` — to'liq ALMASHTIRISH. Drawer
 * shu sababli uchala ro'yxatni birga yuboradi: bittasini alohida
 * saqlash qolgan ikkitasini o'chirib yuborardi.
 */
export function LeadFormAssignmentsDrawer({ form, onClose }: Props) {
	const { t } = useTranslation("leadForms");
	const formId = form?.id ?? null;
	const { data, isLoading } = useLeadFormAssignments(formId);
	const [search, setSearch] = useState("");
	// `useEmployeeOptions` naqshi: har harfda so'rov yuborilmasin.
	const debouncedSearch = useDebouncedValue(search, EMPLOYEE_SEARCH_DEBOUNCE_MS);
	const options = useAssignmentTargetOptions(debouncedSearch);
	const replace = useReplaceLeadFormAssignments(formId);
	const [selection, setSelection] = useState<Selection>(emptySelection);

	/**
	 * ⚠️ **Xodim yorlig'i muammosi — loyihada allaqachon hal qilingan.**
	 * Antd `Select` yorliqni FAQAT `options` ichidan topadi, `GET
	 * /employees` esa `limit: 100` bilan filtrlangan bitta sahifani
	 * qaytaradi. Ya'ni allaqachon biriktirilgan (lekin joriy qidiruv
	 * natijasida bo'lmagan) xodim ko'p tanlashda **xom UUID** bo'lib
	 * ko'rinardi. Yechim `ZoneAssignmentsDrawer` dagi bilan bir xil:
	 * ismlar `GET /employees/{id}` orqali alohida olinadi va tanlangan,
	 * lekin qidiruvda yo'q ID lar variantlar ro'yxatiga qo'shiladi.
	 */
	const employeeNames = useEmployeeNames(selection.employee);

	useEffect(() => {
		if (!data) {
			setSelection(emptySelection());
			return;
		}

		const next = emptySelection();

		for (const item of data) {
			next[item.kind].push(item.targetId);
		}

		setSelection(next);
	}, [data]);

	const handleSave = () => {
		const assignments = (Object.keys(selection) as LeadFormAssignmentKind[]).flatMap((kind) =>
			selection[kind].map((targetId) => ({ kind, targetId }))
		);

		replace.mutate(assignments, { onSuccess: onClose });
	};

	/**
	 * Tanlangan, lekin joriy qidiruv natijasida yo'q xodimlar variantlar
	 * ro'yxatiga qo'shiladi (`withSelectedEmployeeOption` ning ko'p
	 * tanlash uchun moslashtirilgan varianti — o'sha yordamchi bitta
	 * `selectedId` qabul qiladi).
	 */
	const optionsFor = (kind: LeadFormAssignmentKind) => {
		const base = options[kind].map((item) => ({ value: item.id, label: item.name }));

		if (kind !== "employee") {
			return base;
		}

		const present = new Set(base.map((option) => option.value));
		const missing = selection.employee
			.filter((id) => !present.has(id))
			.map((id) => ({ value: id, label: employeeNames.get(id) ?? id }));

		return [...missing, ...base];
	};

	return (
		<Drawer
			open={form !== null}
			onClose={onClose}
			width={520}
			title={form ? t("assignments.titleWithName", { name: form.name }) : t("assignments.title")}
			extra={
				<Space>
					<Button onClick={onClose}>{t("assignments.cancel")}</Button>
					<Button type="primary" loading={replace.isPending} onClick={handleSave}>
						{t("assignments.save")}
					</Button>
				</Space>
			}
		>
			{isLoading ? (
				<Spin />
			) : (
				<Form layout="vertical">
					<Typography.Paragraph type="secondary">
						{t("assignments.description")}
					</Typography.Paragraph>
					{ASSIGNMENT_KINDS.map((kind) => (
						<Form.Item key={kind} label={t(`assignments.kind.${kind}`)}>
							<Select
								mode="multiple"
								allowClear
								showSearch
								filterOption={kind !== "employee"}
								onSearch={kind === "employee" ? setSearch : undefined}
								value={selection[kind]}
								onChange={(value: string[]) =>
									setSelection((current) => ({ ...current, [kind]: value }))
								}
								options={optionsFor(kind)}
								placeholder={t(`assignments.placeholder.${kind}`)}
							/>
						</Form.Item>
					))}
				</Form>
			)}
		</Drawer>
	);
}
